import { FC } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import { ReactComponent as DraggableDotsIcon } from "src/assets/draggable-dots.svg";
import { ReactComponent as DraggableDotsWhiteIcon } from "src/assets/draggable-dots-white.svg";
import { ReactComponent as ValidatorIcon } from "src/assets/validator.svg";
import { ReactComponent as ValidatorWhiteIcon } from "src/assets/validator-white.svg";
import { useDrag } from "react-dnd";
import { useStore, connect } from "src/store/connect";
import { BlockTypesLabels } from "src/config/constants";
import mixpanel from "mixpanel-browser";

interface BlockProps {
  blockType: BlockType;
  draggable: boolean;
  theme?: "dark" | "light";
  label?: string;
  description?: string;
  open?: boolean;
  openMenu?: () => void;
}

interface DropResult {
  id: string;
}

const Block: FC<BlockProps> = ({
  blockType,
  draggable,
  theme,
  label,
  description,
  open,
  openMenu,
}) => {
  const classes = useStyles();
  const state = useStore();

  const [{}, drag] = useDrag(() => ({
    type: "block",
    item: { blockType },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult<DropResult>();

      if (item && dropResult) {
        state.changeBlockType(dropResult.id, item.blockType);
        // TODO: open the edit modal after creating the new block.

        mixpanel.track('block_added', { block_type: blockType, })
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }));

  return (
    <>
      {draggable ? (
        <div
          className={`${classes.root} ${
            theme === "dark" ? classes.rootDark : ""
          } ${description !== "" ? classes.oneRow : ""}`}
          ref={drag}
          role="block"
        >
          {theme === "dark" ? (
            <DraggableDotsWhiteIcon className={classes.dots} />
          ) : (
            <DraggableDotsIcon className={classes.dots} />
          )}
          <Typography className={classes.label}>
            {BlockTypesLabels[blockType]}
          </Typography>
          <Typography className={classes.description}>{description}</Typography>
        </div>
      ) : (
        <div
          className={`${classes.root} ${open ? classes.menuOpened : ""}`}
          onClick={openMenu}
        >
          {theme === "dark" || open ? (
            <ValidatorWhiteIcon className={classes.dots} />
          ) : (
            <ValidatorIcon className={classes.dots} />
          )}
          <Typography className={classes.label}>{label}</Typography>
          <Typography className={classes.description}>{description}</Typography>
        </div>
      )}
    </>
  );
};

Block.defaultProps = {
  draggable: true,
  theme: "light",
  open: false,
};

export default connect(Block);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "36px 1fr",
    rowGap: "5px",
    maxWidth: "324px",
    backgroundColor: "#f8f8f8",
    padding: "10px 20px 9px 16px",
    borderRadius: "4px",
    cursor: "move",
    margin: "0 13px 0 0",
  },
  rootDark: {
    backgroundColor: "#fff",
    "&:hover": {
      color: "#fff",
      backgroundColor: "#6563ff",
    },
  },
  menuOpened: {
    color: "#fff",
    backgroundColor: "#6563ff",
  },
  oneRow: {
    gridTemplateRows: "auto",
  },
  dots: {
    gridRow: "1 / span 2",
    margin: "5px 16px 0 0",
  },
  label: {
    fontWeight: 500,
  },
  description: {
    gridColumn: "2",
    fontWeight: 400,
  },
}));
